import React, { useEffect, useState } from 'react'
import { Form, Modal } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import validator from 'validator'

import {
  UsagePermissionRequestDto,
  UsagePermissionResponseDto,
} from '../../api/kmsapiclient'
import { updateUsagePermissionAsync } from '../../api/usagePermissionApi'
import MultiInputTextField from '../../components/common/MultiInputTextField'
import { UPDATE_USAGE_PERMISSION } from '../../redux/actions/actionTypes'
import {
  apiCallError,
  beginApiCall,
  endApiCall,
} from '../../redux/actions/apiStatusActions'
import { updateUsagePermissionSuccess } from '../../redux/actions/usagePermissionsActions'
import { store, useAppDispatch } from '../../redux/configureStore'
import { isValidInput } from '../../utils'
import { usePermissions } from '../common/permission/usePermissions'
import TextInput from '../common/TextInput'

import { UsagePermissionsInfo } from './types'

type UsagePermissionFormProps = {
  entityData: UsagePermissionsInfo
  history?: {
    push: (path: string) => void
  }
  onClose: () => void
}

const UsagePermissionForm = ({
  entityData,
  history,
  onClose,
}: UsagePermissionFormProps) => {
  const allowedChars = ' -'
  const [usageErrorMessage, setUsageErrorMessage] = useState('')
  const [entraIdGroupErrorMessage, setEntraIdGroupErrorMessage] = useState('')
  const [emailsErrorMessage, setEmailsErrorMessage] = useState('')
  const [usagePermission, setUsagePermission] =
    useState<UsagePermissionsInfo>(entityData)
  const [emailsString, setEmailsString] = useState(entityData.emails)
  const [dataChanged, setDataChanged] = useState(false)
  const [formReady, setFormReady] = useState(false)
  const [rendered, setRendered] = useState(false)
  const usagePermissionsPath = '/usagepermissions'

  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { checkPermissions } = usePermissions()

  useEffect(() => {
    setRendered(true)
  }, [])

  useEffect(() => {
    setUsagePermission(entityData)
    setEmailsString(entityData.emails)
  }, [entityData])

  useEffect(() => {
    if (rendered) {
      validateForm(emailsString)
    }
    // This UseEffect is called when the emailsString is updated as a workaround to the use of the MultiInputTextField component
    setUsagePermission({
      ...usagePermission,
      emails: emailsString,
    })
  }, [emailsString])

  const submitForm = async () => {
    // Disable the Add button
    setDataChanged(false)

    await updateUsagePermission(usagePermission)

    if (
      history !== undefined &&
      history !== null &&
      history.push !== undefined &&
      history.push !== null
    ) {
      history.push(usagePermissionsPath)
    }

    //Set form back to note ready
    setFormReady(false)
  }

  const isFormValid = (currentEmails = ''): boolean => {
    // Clear error Message
    setUsageErrorMessage('')
    setEmailsErrorMessage('')
    setEntraIdGroupErrorMessage('')

    let hasErrors = false

    // Get Props email value if currentEmails is empty
    currentEmails = currentEmails === '' ? emailsString : currentEmails

    if (usagePermission.usage.trim() !== '') {
      if (!isValidInput(usagePermission.usage, allowedChars)) {
        // Invalid usage error message for alphanumeric and spaces
        setUsageErrorMessage(t('usage_permission.invaild'))
        hasErrors = true
      }
    } else {
      setUsageErrorMessage(t('usage_permission.required'))
      hasErrors = true
    }

    if (
      currentEmails.trim() === '' &&
      usagePermission.entraIDGroup.trim() === ''
    ) {
      setEmailsErrorMessage(t('usage_permission.aad_or_email'))
      setEntraIdGroupErrorMessage(t('usage_permission.aad_or_email'))
      hasErrors = true
    } else if (usagePermission.entraIDGroup.trim() !== '') {
      if (!isValidInput(usagePermission.entraIDGroup, allowedChars)) {
        // Invalid usage error message for alphanumeric and spaces
        setEntraIdGroupErrorMessage(
          t('usage_permission.invalid_aad_revocation'),
        )
        hasErrors = true
      }
    }

    return !hasErrors
  }

  const validateForm = (currentEmails: string) => {
    if (isFormValid(currentEmails)) {
      setFormReady(true)
    } else setFormReady(false)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setUsagePermission({ ...usagePermission, [name]: value })
    if (rendered) {
      setDataChanged(true)
    }
  }

  const updateUsagePermission = async (
    usagePermission: UsagePermissionsInfo,
  ) => {
    checkPermissions(store.getState(), UPDATE_USAGE_PERMISSION)

    dispatch(beginApiCall())
    const usagePermissionRequestDto: UsagePermissionRequestDto = {
      usage: usagePermission.usage,
      entraIDGroup: usagePermission.entraIDGroup,
      emails: usagePermission.emails,
    }

    try {
      const result: UsagePermissionResponseDto =
        await updateUsagePermissionAsync(
          usagePermission.id,
          usagePermissionRequestDto,
        )
      if (result != null) {
        dispatch(updateUsagePermissionSuccess(usagePermission))
        toast.success(t('usage_permission.updated'))
      } else {
        dispatch(endApiCall())
        toast.error(t('usage_permission.update_failed'))
      }
    } catch (error) {
      toast.error(t('usage_permission.update_failed'))
      dispatch(apiCallError())
      throw error
    }
  }

  const updateValidEmails = (emails: string[]): boolean => {
    setEmailsErrorMessage('')
    let isValid = false
    if (emails.length > 0) {
      let invalidEmailFound = false
      for (const email of emails) {
        if (validator.isEmail(email) === false) {
          invalidEmailFound = true
          //remove email that failed validation
          const emailsToUpdate = [...emails]
          const index = emailsToUpdate.indexOf(email)
          emailsToUpdate.splice(index, 1)
          const updatedEmails = [...emailsToUpdate]

          const emailString = updatedEmails.join(',')
          setEmailsString(emailString)

          setEmailsErrorMessage(t('usage_permission.invalid_email'))
          break
        }
      }

      if (!invalidEmailFound) {
        setEmailsString(emails.join(','))
        isValid = true
      }
    } else {
      setEmailsString('')
    }
    return isValid
  }

  const handleSelectedInput = (items: string[]) => {
    updateValidEmails(items)
    setDataChanged(true)
  }

  return (
    <>
      <Modal.Header className="responsive-button" closeButton>
        <Modal.Title>{t('usage_permission.edit')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form.Group controlId="formFile" className="mb-3">
          <TextInput
            name="usage"
            label={t('usage_permission.name')}
            value={usagePermission.usage}
            onKeyUp={() => validateForm('')}
            onChange={handleChange}
            error={usageErrorMessage}
            disabled={!!usagePermission?.id}
            maxLength={20}
          />
        </Form.Group>
        <Form.Group controlId="formFile" className="mb-3">
          <TextInput
            name="entraIDGroup"
            label={t('key.aad_revocation')}
            value={usagePermission.entraIDGroup}
            onKeyUp={() => validateForm('')}
            onChange={handleChange}
            error={entraIdGroupErrorMessage}
          />
        </Form.Group>
        <Form.Group controlId="formFile" className="mb-3 responsive-font">
          <MultiInputTextField
            selectedInput={handleSelectedInput}
            name="emails"
            placeholder={t('usage_permission.enter_email')}
            label={t('usage_permission.email_address')}
            value={emailsString}
            inputs={emailsString?.split(',')}
            validateFn={updateValidEmails}
            error={emailsErrorMessage}
            tooltip={t('usage_permission.email_tooltip')}
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer>
        <button
          className="responsive-button btn btn-secondary"
          onClick={() => onClose()}
        >
          {t('close')}
        </button>
        <button
          disabled={!(dataChanged && formReady)}
          className="responsive-button btn btn-primary"
          onClick={submitForm}
        >
          {t('save')}
        </button>
      </Modal.Footer>
    </>
  )
}
UsagePermissionForm.displayName = 'UsagePermissionForm'

export default UsagePermissionForm
